import { createContext, useContext, type ReactNode } from 'react';
import {
	useAlertSubscription
} from '@/hooks/useAlertSubscription';
import type { AlertCreatedPayload } from '@/components/alerts/alerts.gql';

interface AlertSubscriptionContextValue {
	/** The latest alert received from subscription */
	latestAlert: AlertCreatedPayload | null;
	/** Whether the subscription is currently loading */
	isLoading: boolean;
	/** Any error from the subscription */
	error: Error | undefined;
	/** Whether currently subscribed (authenticated and connected) */
	isSubscribed: boolean;
	/** Manually trigger alerts refresh */
	refreshAlerts: () => void;
}

const AlertSubscriptionContext = createContext<
	AlertSubscriptionContextValue | undefined
>(undefined);

interface AlertSubscriptionProviderProps {
	children: ReactNode;
}

/**
 * Provider component that manages alert subscriptions at the app level.
 * Should be placed inside AuthProvider but wrapping the main app content.
 * Automatically handles subscribe/unsubscribe based on authentication state.
 */
export function AlertSubscriptionProvider({
	children
}: AlertSubscriptionProviderProps) {
	const subscriptionState = useAlertSubscription({
		showToast: true,
		refreshAlerts: true
	});

	return (
		<AlertSubscriptionContext.Provider value={subscriptionState}>
			{children}
		</AlertSubscriptionContext.Provider>
	);
}

/**
 * Hook to access the alert subscription context.
 * Use this to check subscription status or manually refresh alerts.
 */
export function useAlertSubscriptionContext() {
	const context = useContext(AlertSubscriptionContext);

	if (context === undefined) {
		throw new Error(
			'useAlertSubscriptionContext must be used within an AlertSubscriptionProvider'
		);
	}

	return context;
}
